import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Users, Check, X, ArrowLeft, Calendar, ShoppingBag, DollarSign, BookOpen, Package, AlertTriangle, Plus, Minus, Unlock, PawPrint, Pencil, Trash2, Upload, Heart, ClipboardList, Search, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [inadimplentes, setInadimplentes] = useState([]);
  const [apoiadores, setApoiadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, inadRes, apoRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/pending-users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/inadimplentes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/apoiadores`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data);
      setPendingUsers(usersRes.data.users);
      setInadimplentes(inadRes.data.inadimplentes);
      setApoiadores(apoRes.data.apoiadores);
      setAllUsers(allRes.data.users || []);
    } catch (error) {
      toast.error('Erro ao carregar dados administrativos');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId, nome, hard = false) => {
    const msg = hard
      ? `EXCLUIR PERMANENTEMENTE ${nome}? Esta ação NÃO pode ser desfeita.`
      : `Remover ${nome} do MC? (soft-delete: o cadastro não some, fica com status=removida)`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}${hard ? '?hard=true' : ''}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(hard ? 'Cadastro excluído permanentemente 🗑️' : 'Integrante removida');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  const handleResetPassword = async (userId, nome) => {
    if (!window.confirm(`Resetar a senha de ${nome}?\n\nUma senha temporária será gerada. Avise a integrante para entrar com ela e trocar no 1º acesso.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/users/${userId}/reset-password`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const senha = r.data.senha_temporaria;
      const email = r.data.email;
      window.prompt(
        `✅ Senha de ${nome} resetada!\n\nPasse estes dados pra ela (copie do campo abaixo):`,
        `Email: ${email}\nSenha temporária: ${senha}`
      );
      toast.success('Senha resetada! 🔑');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/approve-user/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Usuária aprovada com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao aprovar usuária');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Tem certeza que deseja rejeitar este cadastro?')) {
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/admin/reject-user/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cadastro rejeitado e removido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao rejeitar usuária');
    }
  };

  const handleMarcarPendente = async (userId) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/users/${userId}/marcar-mensalidade-pendente`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.bloqueada) {
        toast.warning('Atingiu 3 pendências — carteirinha bloqueada automaticamente.');
      } else {
        toast.success('Mensalidade marcada como pendente.');
      }
      fetchData();
    } catch {
      toast.error('Erro ao marcar pendência');
    }
  };

  const handleRegistrarPagamento = async (userId) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/users/${userId}/registrar-pagamento-mensalidade`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.reativada) toast.success('Pagamento registrado. Carteirinha reativada!');
      else toast.success('Pagamento registrado.');
      fetchData();
    } catch {
      toast.error('Erro ao registrar pagamento');
    }
  };

  const handleDesbloquear = async (userId) => {
    if (!window.confirm('Desbloquear esta integrante? Isto zera as mensalidades pendentes.')) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/desbloquear`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Integrante desbloqueada manualmente.');
      fetchData();
    } catch {
      toast.error('Erro ao desbloquear');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E2B84A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FFD700] to-[#F4C430] p-6">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/dashboard" 
            data-testid="admin-back-button"
            className="inline-flex items-center gap-2 text-black hover:text-black/70 mb-4"
          >
            <ArrowLeft size={20} />
            <span>Voltar à Área do Aluno</span>
          </Link>
          <h1 className="text-4xl font-black text-black mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-title">
            Painel Administrativo
          </h1>
          <p className="text-black/70">Gestão do Giraia Bike Shop - Divisão Sorriso-MT</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#120E1A] border-b border-[#2D243F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6 flex-wrap">
            <TabButton 
              active={activeTab === 'overview'} 
              onClick={() => setActiveTab('overview')}
              dataTestId="admin-tab-overview"
            >
              Visão Geral
            </TabButton>
            {false && (<TabButton 
              active={activeTab === 'approvals'} 
              onClick={() => setActiveTab('approvals')}
              dataTestId="admin-tab-approvals"
            >
              Aprovações ({pendingUsers.length})
            </TabButton>)}
            <TabButton 
              active={activeTab === 'integrantes'} 
              onClick={() => setActiveTab('integrantes')}
              dataTestId="admin-tab-integrantes"
            >
              Alunos e clientes ({allUsers.filter(u => u.status !== 'removida').length})
            </TabButton>
            {false && (<TabButton 
              active={activeTab === 'inadimplentes'} 
              onClick={() => setActiveTab('inadimplentes')}
              dataTestId="admin-tab-inadimplentes"
            >
              Mensalidades ({inadimplentes.length})
            </TabButton>)}
            {false && (<TabButton
              active={activeTab === 'caderninho'}
              onClick={() => setActiveTab('caderninho')}
              dataTestId="admin-tab-caderninho"
            >
              💰 Tesouraria
            </TabButton>)}
            <TabButton 
              active={activeTab === 'apoiadores'} 
              onClick={() => setActiveTab('apoiadores')}
              dataTestId="admin-tab-apoiadores"
            >
              Apoiadores 🐾 ({apoiadores.filter(a => a.status === 'novo').length})
            </TabButton>
            {false && (<TabButton
              active={activeTab === 'distribuicoes'}
              onClick={() => setActiveTab('distribuicoes')}
              dataTestId="admin-tab-distribuicoes"
            >
              💰 Pagamento Anual 40%
            </TabButton>)}
            <TabButton
              active={activeTab === 'aniversariantes'}
              onClick={() => setActiveTab('aniversariantes')}
              dataTestId="admin-tab-aniversariantes"
            >
              🎂 Aniversariantes
            </TabButton>
            <Link 
              to="/livro-caixa"
              data-testid="admin-livro-caixa-link"
              className="py-4 px-6 font-bold text-[#FFD700] hover:text-[#F4C430] transition-colors flex items-center gap-2"
            >
              <BookOpen size={18} />
              Livro Caixa
            </Link>
            <Link 
              to="/admin/products"
              data-testid="admin-products-link"
              className="py-4 px-6 font-bold text-[#E2B84A] hover:text-[#F0D98C] transition-colors flex items-center gap-2"
            >
              <Package size={18} />
              Produtos
            </Link>
            <Link 
              to="/admin/acoes-sociais"
              data-testid="admin-acoes-sociais-link"
              className="py-4 px-6 font-bold text-[#E2B84A] hover:text-[#F0D98C] transition-colors flex items-center gap-2"
            >
              <Heart size={18} />
              Ações Sociais
            </Link>
            {false && (<Link
              to="/admin/saas"
              data-testid="admin-saas-link"
              className="py-4 px-6 font-bold bg-gradient-to-r from-[#E2B84A] to-[#FFD700] bg-clip-text text-transparent hover:opacity-80 transition-opacity flex items-center gap-2"
            >
              <DollarSign size={18} className="text-[#FFD700]" />
              Matriz SaaS
            </Link>)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Estatísticas Gerais
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard 
                icon={<Users className="w-6 h-6" />}
                label="Total de cadastros"
                value={stats.total_users}
                dataTestId="admin-stat-total-users"
              />
              <StatCard 
                icon={<Users className="w-6 h-6 text-green-400" />}
                label="Cadastros ativos"
                value={stats.active_users}
                dataTestId="admin-stat-active-users"
              />
              <StatCard 
                icon={<Calendar className="w-6 h-6" />}
                label="Total de Eventos"
                value={stats.total_events}
                dataTestId="admin-stat-total-events"
              />
              <StatCard 
                icon={<DollarSign className="w-6 h-6" />}
                label="Total de Pagamentos"
                value={stats.total_payments}
                dataTestId="admin-stat-total-payments"
              />
            </div>

            {pendingUsers.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                <h3 className="text-yellow-300 font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ⚠️ {pendingUsers.length} Cadastro(s) Pendente(s)
                </h3>
                <p className="text-yellow-200 text-sm">
                  Há cadastros aguardando aprovação. Clique na aba "Aprovações" para revisar.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'approvals' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Cadastros Pendentes de Aprovação
            </h2>

            {pendingUsers.length === 0 ? (
              <div className="text-center py-12 bg-[#120E1A] border border-[#2D243F] rounded-xl">
                <Users className="w-16 h-16 text-[#716A80] mx-auto mb-4" />
                <p className="text-[#A19BAA]">Nenhum cadastro pendente no momento</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map((user) => (
                  <UserApprovalCard 
                    key={user.id} 
                    user={user}
                    onApprove={() => handleApprove(user.id)}
                    onReject={() => handleReject(user.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'integrantes' && (
          <IntegrantesTab
            users={allUsers}
            onEdit={(u) => setEditingUser(u)}
            onRemove={handleRemoveUser}
            onResetPassword={handleResetPassword}
          />
        )}

        {activeTab === 'inadimplentes' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-7 h-7 text-red-400" />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Controle de Mensalidades
              </h2>
            </div>

            <div className="bg-[#120E1A] border border-[#E2B84A]/30 rounded-xl p-4 mb-6 text-sm text-[#A19BAA]">
              <strong className="text-[#FFD700]">Regra:</strong> ao atingir <strong>3 mensalidades em atraso</strong>, a carteirinha é bloqueada automaticamente. O desbloqueio ocorre sozinho quando o saldo zera ou manualmente pelo botão abaixo.
            </div>

            <AvisarMensalidadesCard token={token} totalInadimplentes={inadimplentes.length} />

            {inadimplentes.length === 0 ? (
              <div className="text-center py-12 bg-[#120E1A] border border-[#2D243F] rounded-xl">
                <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <p className="text-[#A19BAA]">Nenhuma integrante com pendências. Tudo em dia! 🎉</p>
              </div>
            ) : (
              <div className="space-y-3">
                {inadimplentes.map((u) => (
                  <InadimplenteCard
                    key={u.id}
                    user={u}
                    onMarcar={() => handleMarcarPendente(u.id)}
                    onPagar={() => handleRegistrarPagamento(u.id)}
                    onDesbloquear={() => handleDesbloquear(u.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'caderninho' && (
          <CaderninhoMensalidades token={token} />
        )}

        {activeTab === 'apoiadores' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <PawPrint className="w-7 h-7 text-[#FFD700]" />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Apoiadores da Causa
              </h2>
            </div>

            <div className="bg-[#120E1A] border border-[#FFD700]/30 rounded-xl p-4 mb-6 text-sm text-[#A19BAA]">
              Pessoas que preencheram o formulário da landing pública para ajudar com animais de rua, doações e adoção.
            </div>

            {apoiadores.length === 0 ? (
              <div className="text-center py-12 bg-[#120E1A] border border-[#2D243F] rounded-xl">
                <PawPrint className="w-16 h-16 text-[#716A80] mx-auto mb-4" />
                <p className="text-[#A19BAA]">Nenhum apoiador cadastrado ainda.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apoiadores.map(a => (
                  <div
                    key={a.id}
                    data-testid={`apoiador-${a.id}`}
                    className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-5 hover:border-[#FFD700]/50 transition-colors"
                  >
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {a.nome}
                          </h3>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            a.tipo === 'doador' ? 'bg-yellow-500/20 text-yellow-300' :
                            a.tipo === 'adotar' ? 'bg-pink-500/20 text-pink-300' :
                            a.tipo === 'ambos' ? 'bg-gradient-to-r from-[#E2B84A]/20 to-[#FFD700]/20 text-white' :
                            'bg-gold-500/20 text-gold-300'
                          }`}>
                            {a.tipo === 'doador' ? '💰 DOADOR' : a.tipo === 'adotar' ? '🏠 ADOÇÃO' : a.tipo === 'ambos' ? '⭐ VÁRIAS FORMAS' : '🤝 VOLUNTÁRIO'}
                          </span>
                          {a.status === 'novo' && <span className="text-xs bg-red-500 text-white font-bold px-2 py-0.5 rounded-full animate-pulse">NOVO</span>}
                        </div>
                        <p className="text-[#A19BAA] text-sm">
                          {a.email && <span>📧 {a.email}</span>}
                          {a.email && a.telefone && ' · '}
                          {a.telefone && <span>📱 {a.telefone}</span>}
                        </p>
                        {a.cidade && <p className="text-[#716A80] text-xs mt-1">📍 {a.cidade}</p>}
                        {a.mensagem && <p className="text-[#E7E3EC] text-sm mt-2 italic bg-[#050505] p-3 rounded-lg border-l-2 border-[#E2B84A]">"{a.mensagem}"</p>}
                        <p className="text-[#716A80] text-xs mt-2">Recebido em {new Date(a.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {a.status === 'novo' && (
                          <button
                            onClick={async () => {
                              try {
                                await axios.patch(`${API_URL}/api/admin/apoiadores/${a.id}`, { status: 'contatado' }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success('Marcado como contatado');
                                fetchData();
                              } catch { toast.error('Erro'); }
                            }}
                            data-testid={`mark-contatado-${a.id}`}
                            className="bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg px-3 py-1.5 text-xs"
                          >
                            ✓ Contatado
                          </button>
                        )}
                        {a.telefone && (
                          <a
                            href={`https://wa.me/55${a.telefone.replace(/\D/g, '')}`}
                            target="_blank" rel="noreferrer"
                            data-testid={`whatsapp-${a.id}`}
                            className="bg-[#25D366] hover:bg-[#1DA851] text-white font-bold rounded-lg px-3 py-1.5 text-xs text-center"
                          >
                            WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'distribuicoes' && (
          <DistribuicoesTab token={token} />
        )}
        {activeTab === 'aniversariantes' && (
          <AniversariantesTab token={token} />
        )}
      </div>
      {editingUser && (
        <EditUserModal
          user={editingUser}
          token={token}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); fetchData(); }}
        />
      )}
    </div>
  );
};

const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const AvisarMensalidadesCard = ({ token, totalInadimplentes }) => {
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  });
  const [apenasInadimplentes, setApenasInadimplentes] = useState(totalInadimplentes > 0);
  const [sendingPush, setSendingPush] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const enviar = async (canal) => {
    const fn = canal === 'push' ? setSendingPush : setSendingEmail;
    const endpoint = canal === 'push'
      ? '/api/admin/push/cobrancas-mensalidade'
      : '/api/admin/email/cobrancas-mensalidade';
    fn(true);
    try {
      const r = await axios.post(`${API_URL}${endpoint}`, {
        mes_referencia: mes,
        apenas_inadimplentes: apenasInadimplentes,
        divisao: null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const { total, enviados, falhas = 0, modo, push_enabled } = r.data;
      if (canal === 'email') {
        toast.success(`📧 ${enviados}/${total} e-mails enviados · modo: ${modo}${falhas ? ` · ${falhas} falhas` : ''}`);
      } else {
        const extra = push_enabled === false ? ' (atenção: push desabilitado no servidor)' : '';
        toast.success(`🔔 ${enviados}/${total} pushes enviados${extra}`);
      }
    } catch (e) {
      toast.error(`Erro ao disparar ${canal === 'push' ? 'push' : 'e-mail'}`);
    } finally {
      fn(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#120E1A] to-[#1C1628] border border-[#E2B84A]/40 rounded-xl p-5 mb-6" data-testid="avisar-mensalidades-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">📢</span>
        <h3 className="text-white font-black text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Avisar os alunos
        </h3>
      </div>
      <p className="text-[#A19BAA] text-sm mb-4">
        Dispara em massa um aviso com o nome da integrante e quantas mensalidades estão em atraso. <strong>Push</strong> chega no celular na hora; <strong>E-mail</strong> é o backup.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-white text-xs font-bold mb-1">Mês de referência</label>
          <input
            type="text"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            data-testid="avisar-mes-input"
            placeholder="MM/YYYY"
            className="w-full bg-[#050505] border border-[#2D243F] rounded-lg px-3 py-2 text-white focus:border-[#E2B84A] outline-none"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer self-end pb-1">
          <input
            type="checkbox"
            checked={apenasInadimplentes}
            onChange={(e) => setApenasInadimplentes(e.target.checked)}
            data-testid="avisar-apenas-inadimp"
            className="w-4 h-4 accent-[#E2B84A]"
          />
          <span className="text-white text-sm">Só avisar as inadimplentes ({totalInadimplentes})</span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => enviar('push')}
          disabled={sendingPush}
          data-testid="avisar-push-button"
          className="flex-1 sm:flex-none bg-[#E2B84A] hover:bg-[#F0D98C] disabled:opacity-50 text-white font-black rounded-lg px-5 py-2.5 flex items-center justify-center gap-2 transition-colors"
        >
          {sendingPush ? '...' : <>🔔 Enviar Push</>}
        </button>
        <button
          onClick={() => enviar('email')}
          disabled={sendingEmail}
          data-testid="avisar-email-button"
          className="flex-1 sm:flex-none bg-[#FFD700] hover:bg-[#F59E0B] disabled:opacity-50 text-black font-black rounded-lg px-5 py-2.5 flex items-center justify-center gap-2 transition-colors"
        >
          {sendingEmail ? '...' : <>📧 Enviar E-mail</>}
        </button>
      </div>
      <p className="text-[#716A80] text-xs mt-3">
        📌 Push só chega nos alunos que <strong>aceitaram notificações</strong> no app. E-mail depende do Resend estar configurado.
      </p>
    </div>
  );
};

const AniversariantesTab = ({ token }) => {
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState({ aniversariantes: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/aniversariantes-do-mes?mes=${mes}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [mes, token]);

  const formatarDia = (iso) => {
    if (!iso) return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${parseInt(m[3], 10)} de ${MESES_PT[parseInt(m[2], 10) - 1]}` : iso;
  };

  return (
    <div data-testid="aniversariantes-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="text-2xl font-black text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          🎂 Aniversariantes de {MESES_PT[mes - 1]}
        </h2>
        <select
          value={mes}
          onChange={(e) => setMes(parseInt(e.target.value, 10))}
          data-testid="aniversariantes-mes-select"
          className="bg-[#120E1A] border border-[#2D243F] rounded-lg px-3 py-2 text-white focus:border-[#E2B84A] outline-none"
        >
          {MESES_PT.map((m, i) => (
            <option key={i} value={i + 1}>{m}</option>
          ))}
        </select>
        <span className="text-[#A19BAA] text-sm">{data.total} mana{data.total === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div className="text-[#A19BAA]">Carregando...</div>
      ) : data.aniversariantes.length === 0 ? (
        <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-10 text-center text-[#A19BAA]" data-testid="aniversariantes-empty">
          <span className="text-5xl block mb-3">🎈</span>
          <p className="font-bold">Ninguém faz aniversário em {MESES_PT[mes - 1]}.</p>
          <p className="text-xs mt-2">A data de nascimento vem do cadastro do curso e do termo da aula prática.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.aniversariantes.map((u) => (
            <div key={u.id} className="bg-[#120E1A] border border-[#E2B84A]/30 rounded-xl p-5 hover:border-[#FFD700]/60 transition-colors" data-testid={`aniversariante-card-${u.id}`}>
              <div className="flex items-center gap-4 mb-3">
                {u.foto_oficial || u.foto_perfil ? (
                  <img
                    src={`${API_URL}/api/files/${u.foto_oficial || u.foto_perfil}`}
                    alt={u.nome_guerra}
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#FFD700]"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#2D243F] border-2 border-dashed border-[#FFD700]/40 flex items-center justify-center text-[#FFD700] text-xl font-black">
                    {(u.nome_guerra || u.nome_completo || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-black text-lg leading-tight truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {u.nome_guerra || (u.nome_completo || '').split(' ')[0]}
                  </p>
                  <p className="text-[#A19BAA] text-xs truncate">{u.nome_completo}</p>
                  <p className="text-[#FFD700] text-xs font-bold mt-1">📅 {formatarDia(u.data_nascimento)}</p>
                </div>
              </div>
              {u.telefone && (
                <p className="text-[#A19BAA] text-xs mb-2">📞 {u.telefone}</p>
              )}
              {!(u.foto_oficial || u.foto_perfil) && (
                <p className="text-yellow-400/80 text-[10px] mb-2">⚠ Sem foto oficial — peça pra ela enviar pelo perfil.</p>
              )}
              <Link
                to="/flyers"
                data-testid={`aniversariante-flyer-${u.id}`}
                className="block text-center bg-gradient-to-r from-[#E2B84A] to-[#FFD700] text-white text-sm font-black rounded-lg px-3 py-2 hover:opacity-90 transition-all"
              >
                🎨 Gerar Flyer
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DistribuicoesTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/distribuicoes/acumulado`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(r.data);
    } catch (e) {
      toast.error('Erro ao carregar acumulado');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const pagarUma = async (user_id, nome) => {
    if (!window.confirm(`Pagar o acumulado de ${nome}? O valor entra como SAÍDA no Livro Caixa.`)) return;
    setPaying(s => ({ ...s, [user_id]: true }));
    try {
      const r = await axios.post(`${API_URL}/api/admin/distribuicoes/pagar/${user_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(r.data.message || 'Pagamento registrado ✅');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao pagar');
    } finally { setPaying(s => ({ ...s, [user_id]: false })); }
  };

  const pagarTodos = async () => {
    if (!window.confirm(`PAGAR o acumulado de TODAS as ${data?.num_integrantes || 0} integrantes?\n\nTotal: R$ ${data?.total_geral_a_pagar?.toFixed(2).replace('.', ',')}\n\nIsso lança saídas no Livro Caixa.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/distribuicoes/pagar-todos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(r.data.message || `Pagamento massa efetuado: ${r.data.total_pago} integrantes`, { duration: 5000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao pagar massa');
    }
  };

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#FFD700] mx-auto"></div></div>;
  if (!data) return null;
  const { integrantes = [], total_geral_a_pagar = 0, num_integrantes = 0 } = data;

  return (
    <div data-testid="distribuicoes-tab">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <DollarSign className="w-7 h-7 text-[#FFD700]" />
        <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Pagamento Anual 40%
        </h2>
        <span className="bg-[#FFD700]/20 text-[#FFD700] text-xs font-black px-3 py-1 rounded-full">FIM DE ANO</span>
      </div>

      <div className="bg-gradient-to-r from-[#FFD700]/10 to-[#E2B84A]/10 border border-[#FFD700]/40 rounded-xl p-5 mb-6">
        <p className="text-[#E7E3EC] text-sm mb-2">
          💡 <strong className="text-[#FFD700]">Regra:</strong> os 40% das Ações Sociais acumulam o ano todo.
          No fim do ano (ou quando você decidir), pague o acumulado de cada integrante — vira <strong>saída no Livro Caixa</strong>.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-[#050505] rounded-lg p-3">
            <p className="text-[#A19BAA] text-xs">Integrantes com saldo</p>
            <p className="text-white text-2xl font-black">{num_integrantes}</p>
          </div>
          <div className="bg-[#050505] rounded-lg p-3">
            <p className="text-[#A19BAA] text-xs">Total a pagar</p>
            <p className="text-[#FFD700] text-2xl font-black">R$ {total_geral_a_pagar.toFixed(2).replace('.', ',')}</p>
          </div>
        </div>
      </div>

      {integrantes.length === 0 ? (
        <div className="text-center py-12 bg-[#120E1A] border border-[#2D243F] rounded-xl">
          <Check className="w-16 h-16 text-green-400 mx-auto mb-3" />
          <p className="text-[#A19BAA]">Nenhuma integrante com saldo a receber. Tudo quitado! 🎉</p>
          <p className="text-[#716A80] text-xs mt-2">Quando as Ações Sociais forem distribuídas com 30/30/40, os 40% aparecem aqui.</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={pagarTodos}
              data-testid="pagar-todos-btn"
              className="bg-gradient-to-r from-[#FFD700] to-[#F4C430] hover:brightness-110 text-black font-black rounded-lg px-6 py-3 text-sm flex items-center gap-2 shadow-[0_0_24px_rgba(255,215,0,0.3)]"
            >
              💰 Pagar TODAS as {num_integrantes} integrantes (R$ {total_geral_a_pagar.toFixed(2).replace('.', ',')})
            </button>
          </div>
          <div className="space-y-3">
            {integrantes.map(i => (
              <div key={i.user_id} data-testid={`distribuicao-${i.user_id}`}
                className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-4 flex flex-wrap items-center gap-3">
                {i.foto_perfil ? (
                  <img src={`${API_URL}${i.foto_perfil}`} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#2D243F] flex items-center justify-center text-white font-black">
                    {(i.nome_guerra || i.nome_completo || '?')[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-black text-sm truncate">{i.nome_completo}</p>
                  <p className="text-[#A19BAA] text-xs">
                    {i.nome_guerra && <span>{i.nome_guerra} · </span>}
                    {i.matricula} · {i.num_acoes_participadas} {i.num_acoes_participadas === 1 ? 'ação' : 'ações'}
                  </p>
                  {i.chave_pix && <p className="text-[#10B981] text-xs mt-0.5">🔑 PIX: {i.chave_pix}</p>}
                </div>
                <div className="text-right">
                  <p className="text-[#FFD700] text-xl font-black">R$ {i.total_acumulado.toFixed(2).replace('.', ',')}</p>
                </div>
                <button
                  onClick={() => pagarUma(i.user_id, i.nome_completo)}
                  disabled={paying[i.user_id]}
                  data-testid={`pagar-${i.user_id}`}
                  className="bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-lg px-4 py-2 text-sm disabled:opacity-60"
                >
                  {paying[i.user_id] ? '...' : '💰 Pagar'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const IntegrantesTab = ({ users, onEdit, onRemove, onResetPassword }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todas');
  const filtered = users.filter(u => {
    if (statusFilter !== 'todas' && u.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (u.nome_completo || '').toLowerCase().includes(s) ||
      (u.nome_guerra || '').toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s) ||
      (u.matricula || '').toLowerCase().includes(s)
    );
  });
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Alunos e clientes
      </h2>
      <div className="flex flex-wrap gap-2 mb-5">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="integrantes-search"
          placeholder="Buscar por nome, guerra, e-mail ou matrícula…"
          className="flex-1 min-w-[220px] bg-[#050505] border border-[#2D243F] rounded-lg px-4 py-2.5 text-white text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="integrantes-status-filter"
          className="bg-[#050505] border border-[#2D243F] rounded-lg px-3 py-2.5 text-white text-sm"
        >
          <option value="todas">Todas</option>
          <option value="ativa">Ativas</option>
          <option value="pendente">Pendentes</option>
          <option value="bloqueada">Bloqueadas</option>
          <option value="inadimplente">Inadimplentes</option>
          <option value="removida">Removidas</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-10 bg-[#120E1A] border border-[#2D243F] rounded-xl">
          <p className="text-[#A19BAA]">Nenhuma integrante encontrada.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.map(u => (
            <div key={u.id} data-testid={`integrante-card-${u.id}`} className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-4 flex gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-[#E2B84A]/20 flex-shrink-0 flex items-center justify-center">
                {u.foto_perfil ? (
                  <img src={`${API_URL}${u.foto_perfil}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[#FFD700] font-black text-xl">{((u.nome_completo || '?')[0] || '?').toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-white font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {u.nome_completo || <span className="text-red-400 italic">(nome em branco)</span>}
                  </h3>
                  <StatusPill status={u.status} />
                </div>
                <p className="text-[#A19BAA] text-xs truncate">
                  {u.nome_guerra ? `"${u.nome_guerra}"` : 'Cliente'} · {u.email || 'sem e-mail'}
                </p>
                <p className="text-[#716A80] text-xs truncate">
                  {u.matricula} · {u.divisao || '-'} {u.cargo && `· ${u.cargo}`}
                </p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button onClick={() => onEdit(u)} data-testid={`edit-user-${u.id}`}
                    className="text-[#E2B84A] hover:text-[#F0D98C] text-xs font-bold flex items-center gap-1">
                    <Pencil size={12} /> Editar
                  </button>
                  {u.role !== 'admin' && u.status !== 'removida' && (
                    <button onClick={() => onResetPassword(u.id, u.nome_completo || u.email)} data-testid={`reset-password-${u.id}`}
                      className="text-[#10B981] hover:text-[#059669] text-xs font-bold flex items-center gap-1">
                      🔑 Resetar senha
                    </button>
                  )}
                  {u.role !== 'admin' && u.status !== 'removida' && (
                    <button onClick={() => onRemove(u.id, u.nome_completo || u.email)} data-testid={`remove-user-${u.id}`}
                      className="text-red-400 hover:text-red-300 text-xs font-bold flex items-center gap-1">
                      <Trash2 size={12} /> Remover
                    </button>
                  )}
                  {u.role !== 'admin' && u.status === 'removida' && (
                    <button onClick={() => onRemove(u.id, u.nome_completo || u.email, true)} data-testid={`hard-delete-user-${u.id}`}
                      className="text-red-500 hover:text-red-400 text-xs font-black flex items-center gap-1">
                      <Trash2 size={12} /> Excluir definitivamente
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    ativa: 'bg-green-500/20 text-green-400',
    pendente: 'bg-yellow-500/20 text-yellow-400',
    bloqueada: 'bg-red-500/20 text-red-400',
    inadimplente: 'bg-orange-500/20 text-orange-400',
    removida: 'bg-gray-500/20 text-gray-400',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-500/20 text-gray-400'}`}>{(status || '').toUpperCase()}</span>;
};

const EditUserModal = ({ user, token, onClose, onSaved }) => {
  const [form, setForm] = useState({
    nome_completo: user.nome_completo || '',
    nome_guerra: user.nome_guerra || '',
    cargo: user.cargo || '',
    telefone: user.telefone || '',
    email: user.email || '',
    endereco: user.endereco || '',
    tipo_sanguineo: user.tipo_sanguineo || '',
    data_nascimento: user.data_nascimento || '',
    divisao: user.divisao || '',
    tem_moto: user.tem_moto ?? true,
    placa: user.placa || '',
    modelo_moto: user.modelo_moto || '',
    chave_pix: user.chave_pix || '',
    status: user.status || 'ativa',
    role: user.role || 'integrante',
    cpf: user.cpf || '',
    rg: user.rg || '',
  });
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(user.foto_perfil ? `${API_URL}${user.foto_perfil}` : null);
  const [saving, setSaving] = useState(false);

  const handleFoto = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Foto > 5MB'); return; }
    setFoto(f);
    setFotoPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!form.nome_completo.trim()) { toast.error('Nome completo é obrigatório'); return; }
    setSaving(true);
    try {
      let payload = { ...form };
      if (foto) {
        const fd = new FormData();
        fd.append('file', foto);
        fd.append('tipo', 'foto_perfil');
        const up = await axios.post(`${API_URL}/api/admin/comprovantes/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        payload.foto_perfil = up.data.path;
      }
      await axios.patch(`${API_URL}/api/admin/users/${user.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Integrante atualizada ✏️');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const SANGUE_OPTS = [['', '—'], ['A+','A+'], ['A-','A-'], ['B+','B+'], ['B-','B-'], ['AB+','AB+'], ['AB-','AB-'], ['O+','O+'], ['O-','O-']];

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="edit-user-modal">
      <div className="bg-[#120E1A] border-2 border-[#E2B84A] rounded-2xl max-w-2xl w-full my-8 max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#120E1A] border-b border-[#2D243F] p-5 flex justify-between items-center z-10">
          <div>
            <h3 className="text-xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Editar Integrante</h3>
            <p className="text-[#716A80] text-xs">{user.matricula}</p>
          </div>
          <button onClick={onClose} data-testid="close-edit-user" className="text-[#A19BAA]"><X size={22} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-[#050505] border-2 border-dashed border-[#2D243F] overflow-hidden flex items-center justify-center">
              {fotoPreview ? <img src={fotoPreview} alt="" className="w-full h-full object-cover" /> : <Upload className="text-[#716A80]" />}
            </div>
            <label className="cursor-pointer bg-[#E2B84A] hover:bg-[#F0D98C] text-white font-bold rounded-lg px-4 py-2 text-sm">
              Trocar Foto
              <input type="file" accept="image/*" className="hidden" onChange={handleFoto} data-testid="edit-user-foto" />
            </label>
          </div>

          <InputRow>
            <Field label="Nome Completo *" value={form.nome_completo} onChange={v => setForm({...form, nome_completo: v})} testid="edit-nome" />
            <Field label="Nome de Guerra" value={form.nome_guerra} onChange={v => setForm({...form, nome_guerra: v})} testid="edit-guerra" />
          </InputRow>
          <InputRow>
            <Field label="Cargo" value={form.cargo} onChange={v => setForm({...form, cargo: v})} testid="edit-cargo" placeholder="Presidente, Diretora, ADM, Social..." />
            <Select label="Status" value={form.status} onChange={v => setForm({...form, status: v})} testid="edit-status"
              options={[['ativa','Ativa'],['pendente','Pendente'],['bloqueada','Bloqueada'],['inadimplente','Inadimplente'],['removida','Removida']]} />
          </InputRow>
          <InputRow>
            <Field label="E-mail" value={form.email} onChange={v => setForm({...form, email: v})} testid="edit-email" type="email" />
            <Field label="Telefone" value={form.telefone} onChange={v => setForm({...form, telefone: v})} testid="edit-tel" />
          </InputRow>
          <InputRow>
            <Field label="Divisão" value={form.divisao} onChange={v => setForm({...form, divisao: v})} testid="edit-divisao" />
            <Select label="Tipo Sanguíneo" value={form.tipo_sanguineo} onChange={v => setForm({...form, tipo_sanguineo: v})} testid="edit-sangue" options={SANGUE_OPTS} />
          </InputRow>
          <Field label="🎂 Data de Nascimento" value={form.data_nascimento} onChange={v => setForm({...form, data_nascimento: v})} testid="edit-data-nasc" type="date" />
          <Field label="Endereço" value={form.endereco} onChange={v => setForm({...form, endereco: v})} testid="edit-endereco" />
          <InputRow>
            <Field label="CPF" value={form.cpf} onChange={v => setForm({...form, cpf: v})} testid="edit-cpf" />
            <Field label="RG" value={form.rg} onChange={v => setForm({...form, rg: v})} testid="edit-rg" />
          </InputRow>
          <Field label="Chave PIX" value={form.chave_pix} onChange={v => setForm({...form, chave_pix: v})} testid="edit-pix" />

          <div className="border-t border-[#2D243F] pt-3">
            <label className="inline-flex items-center gap-2 mb-3">
              <input type="checkbox" checked={form.tem_moto} onChange={(e) => setForm({...form, tem_moto: e.target.checked})} data-testid="edit-tem-moto" />
              <span className="text-white text-sm">Possui moto</span>
            </label>
            {form.tem_moto && (
              <InputRow>
                <Field label="Modelo da Moto" value={form.modelo_moto} onChange={v => setForm({...form, modelo_moto: v})} testid="edit-modelo" />
                <Field label="Placa" value={form.placa} onChange={v => setForm({...form, placa: v.toUpperCase()})} testid="edit-placa" />
              </InputRow>
            )}
          </div>

          <div className="border-t border-[#2D243F] pt-3">
            <Select label="Função (role)" value={form.role} onChange={v => setForm({...form, role: v})} testid="edit-role"
              options={[['integrante','Integrante'],['diretoria','Diretoria'],['admin','Admin (acesso total)']]} />
            <p className="text-[#716A80] text-xs mt-1">⚠️ Role 'admin' dá acesso total. Use com cuidado.</p>
          </div>

          <button onClick={save} disabled={saving} data-testid="save-edit-user"
            className="w-full bg-[#E2B84A] hover:bg-[#F0D98C] text-white font-black rounded-lg py-3 disabled:opacity-60">
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InputRow = ({ children }) => <div className="grid md:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, value, onChange, testid, type = 'text', placeholder = '' }) => (
  <div>
    <label className="block text-white text-sm font-bold mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid} placeholder={placeholder}
      className="w-full bg-[#050505] border border-[#2D243F] rounded-lg px-3 py-2 text-white text-sm" />
  </div>
);
const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-white text-sm font-bold mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-[#050505] border border-[#2D243F] rounded-lg px-3 py-2 text-white text-sm">
      {options.map(([v, lab], i) => <option key={i} value={v}>{lab}</option>)}
    </select>
  </div>
);

const InadimplenteCard = ({ user, onMarcar, onPagar, onDesbloquear }) => {
  const pend = user.mensalidades_pendentes || 0;
  const blocked = user.status === 'bloqueada';
  return (
    <div
      className={`bg-[#120E1A] border rounded-xl p-5 ${blocked ? 'border-red-500' : 'border-[#2D243F]'}`}
      data-testid={`inadimplente-${user.id}`}
    >
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {user.nome_completo}
            </h3>
            {blocked && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                BLOQUEADA
              </span>
            )}
          </div>
          <p className="text-[#A19BAA] text-sm">{user.email} · {user.divisao} · {user.matricula}</p>
          {user.motivo_bloqueio && (
            <p className="text-red-300 text-xs mt-1">Motivo: {user.motivo_bloqueio}</p>
          )}
        </div>
        <div className={`text-2xl font-black ${pend >= 3 ? 'text-red-400' : pend >= 2 ? 'text-orange-400' : 'text-yellow-400'}`}>
          {pend} / 3
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          onClick={onPagar}
          data-testid={`pagar-${user.id}`}
          disabled={pend === 0}
          className="bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Minus size={14} />
          Registrar Pagamento
        </button>
        <button
          onClick={onMarcar}
          data-testid={`marcar-${user.id}`}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg px-3 py-2 text-sm flex items-center gap-2"
        >
          <Plus size={14} />
          + 1 Mensalidade Pendente
        </button>
        {blocked && (
          <button
            onClick={onDesbloquear}
            data-testid={`desbloquear-${user.id}`}
            className="bg-[#E2B84A] hover:bg-[#F0D98C] text-white font-bold rounded-lg px-3 py-2 text-sm flex items-center gap-2"
          >
            <Unlock size={14} />
            Desbloquear (zera tudo)
          </button>
        )}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, dataTestId }) => {
  return (
    <button
      onClick={onClick}
      data-testid={dataTestId}
      className={`py-4 px-6 font-bold border-b-2 transition-colors ${
        active 
          ? 'border-[#E2B84A] text-[#E2B84A]' 
          : 'border-transparent text-[#716A80] hover:text-[#A19BAA]'
      }`}
    >
      {children}
    </button>
  );
};

const StatCard = ({ icon, label, value, dataTestId }) => {
  return (
    <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-6" data-testid={dataTestId}>
      <div className="flex items-center gap-3 mb-3">
        <div className="text-[#E2B84A]">{icon}</div>
      </div>
      <p className="text-[#A19BAA] text-sm mb-1">{label}</p>
      <p className="text-3xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {value}
      </p>
    </div>
  );
};

const UserApprovalCard = ({ user, onApprove, onReject }) => {
  return (
    <div 
      className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-6"
      data-testid={`approval-card-${user.id}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {user.nome_completo}
          </h3>
          <p className="text-[#A19BAA] text-sm">{user.email}</p>
        </div>
        <span className="bg-yellow-500/20 text-yellow-300 px-3 py-1 rounded-full text-xs font-bold">
          Pendente
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <InfoRow label="CPF" value={user.cpf} />
        <InfoRow label="RG" value={user.rg} />
        <InfoRow label="Telefone" value={user.telefone} />
        <InfoRow label="Divisão" value={user.divisao} />
        <InfoRow label="Modelo da Moto" value={user.modelo_moto} />
        <InfoRow label="Placa" value={user.placa_moto} />
        <InfoRow label="Como Conheceu" value={user.como_conheceu} />
        {user.nome_madrinha && <InfoRow label="Madrinha" value={user.nome_madrinha} />}
      </div>

      <div className="flex gap-3">
        <button
          onClick={onApprove}
          data-testid={`approve-button-${user.id}`}
          className="flex-1 bg-green-500 text-white font-bold rounded-lg px-6 py-3 hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
        >
          <Check size={20} />
          Aprovar
        </button>
        <button
          onClick={onReject}
          data-testid={`reject-button-${user.id}`}
          className="flex-1 bg-red-500 text-white font-bold rounded-lg px-6 py-3 hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
        >
          <X size={20} />
          Rejeitar
        </button>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => {
  return (
    <div>
      <p className="text-[#716A80] text-xs mb-1">{label}</p>
      <p className="text-white">{value}</p>
    </div>
  );
};

// ============= CADERNINHO DE MENSALIDADES =============
const CaderninhoMensalidades = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas'); // todas | em_dia | atrasadas | bloqueadas
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/mensalidades/controle`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(r.data);
    } catch {
      toast.error('Erro ao carregar caderninho');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const registrarPagamento = async (userId, nome) => {
    if (!window.confirm(`Confirmar pagamento de R$ 60,00 da ${nome}?\n\nIsto zera 1 mensalidade pendente e registra no Livro Caixa.`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/registrar-pagamento-mensalidade`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Pagamento da ${nome} registrado! 💜`);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar');
    }
  };

  const formatarData = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return iso.slice(0, 10); }
  };

  const buscaLower = busca.toLowerCase().trim();
  const manas = (data?.manas || []).filter(m => {
    if (filtro === 'em_dia' && m.status_pagamento !== 'em_dia') return false;
    if (filtro === 'atrasadas' && m.status_pagamento !== 'atrasada') return false;
    if (filtro === 'bloqueadas' && m.status_pagamento !== 'bloqueada') return false;
    if (buscaLower && !(m.nome_completo || '').toLowerCase().includes(buscaLower) &&
        !(m.nome_guerra || '').toLowerCase().includes(buscaLower) &&
        !(m.matricula || '').toLowerCase().includes(buscaLower)) return false;
    return true;
  });

  const exportarCSV = () => {
    if (!data?.manas?.length) return;
    const headers = ['Matrícula', 'Nome Completo', 'Nome de Guerra', 'Divisão', 'Status', 'Mensalidades Pendentes', 'Última Data Pagamento', 'Total Pago no App'];
    const rows = data.manas.map(m => [
      m.matricula || '',
      m.nome_completo || '',
      m.nome_guerra || '',
      m.divisao || '',
      m.status_pagamento === 'em_dia' ? 'Em dia' : m.status_pagamento === 'atrasada' ? 'Atrasada' : 'Bloqueada',
      m.mensalidades_pendentes || 0,
      formatarData(m.ultimo_pagamento_data),
      `R$ ${(m.total_pago_app || 0).toFixed(2)}`,
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `caderninho-mensalidades-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E2B84A]"></div>
    </div>
  );

  return (
    <div data-testid="caderninho-tab">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <ClipboardList className="w-7 h-7 text-[#FFD700]" />
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Tesouraria — Controle de Mensalidades
        </h2>
        <button
          onClick={async () => {
            try {
              const r = await axios.get(`${API_URL}/api/admin/download-codigo`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
              });
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = 'giraia-bike-app-codigo.zip';
              a.click();
              window.URL.revokeObjectURL(url);
              toast.success('Download iniciado! 💾');
            } catch (e) {
              toast.error('Erro ao baixar');
            }
          }}
          data-testid="download-codigo-btn"
          className="ml-2 bg-[#FFD700]/20 hover:bg-[#FFD700]/30 text-[#FFD700] border border-[#FFD700]/40 font-bold rounded-lg px-3 py-2 text-xs flex items-center gap-1"
          title="Baixar código-fonte do app em ZIP"
        >
          💾 Baixar App
        </button>
        <button
          onClick={exportarCSV}
          data-testid="caderninho-export-csv"
          className="ml-auto bg-[#E2B84A]/20 hover:bg-[#E2B84A]/30 text-[#D8B4FE] border border-[#E2B84A]/40 font-bold rounded-lg px-4 py-2 text-sm flex items-center gap-2"
        >
          📥 Exportar Excel
        </button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="caderninho-resumo">
        <ResumoCard label="Total" valor={data?.resumo?.total || 0} cor="text-white" border="border-[#2D243F]" />
        <ResumoCard label="✅ Em dia" valor={data?.resumo?.em_dia || 0} cor="text-green-400" border="border-green-500/40" />
        <ResumoCard label="🔴 Atrasadas" valor={data?.resumo?.atrasadas || 0} cor="text-red-400" border="border-red-500/40" />
        <ResumoCard label="🚫 Bloqueadas" valor={data?.resumo?.bloqueadas || 0} cor="text-orange-400" border="border-orange-500/40" />
      </div>

      {/* Busca + Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#716A80]" size={18} />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou e-mail..."
            data-testid="caderninho-busca"
            className="w-full bg-[#120E1A] border border-[#2D243F] rounded-lg pl-10 pr-4 py-2.5 text-white outline-none focus:border-[#E2B84A]"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {[
            { v: 'todas', l: 'Todas' },
            { v: 'em_dia', l: '✅ Em dia' },
            { v: 'atrasadas', l: '🔴 Atrasadas' },
            { v: 'bloqueadas', l: '🚫 Bloqueadas' },
          ].map(o => (
            <button
              key={o.v}
              onClick={() => setFiltro(o.v)}
              data-testid={`caderninho-filtro-${o.v}`}
              className={`shrink-0 px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
                filtro === o.v
                  ? 'bg-[#E2B84A] text-white'
                  : 'bg-[#120E1A] border border-[#2D243F] text-[#A19BAA] hover:border-[#E2B84A]'
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {manas.length === 0 ? (
        <div className="bg-[#120E1A] border border-[#2D243F] rounded-xl p-10 text-center text-[#A19BAA]">
          <ClipboardList className="w-12 h-12 text-[#716A80] mx-auto mb-3" />
          <p>Nenhuma mana encontrada com esses filtros.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="caderninho-lista">
          {manas.map(m => (
            <div
              key={m.id}
              className={`bg-[#120E1A] border rounded-xl p-4 ${
                m.status_pagamento === 'em_dia' ? 'border-green-500/30' :
                m.status_pagamento === 'atrasada' ? 'border-red-500/40' :
                'border-orange-500/50'
              }`}
              data-testid={`caderninho-mana-${m.id}`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                {/* Foto */}
                <div className="w-12 h-12 rounded-full overflow-hidden bg-[#050505] border border-[#2D243F] flex items-center justify-center shrink-0">
                  {m.foto_perfil ? (
                    <img src={`${API_URL}/api/files/${m.foto_perfil}`} alt={m.nome_completo} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-black text-[#A19BAA]">{(m.nome_guerra || m.nome_completo || '?')[0]}</span>
                  )}
                </div>

                {/* Nome + matrícula */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {m.nome_guerra || m.nome_completo}
                  </p>
                  <p className="text-[#A19BAA] text-xs truncate">
                    {m.matricula} {m.divisao && `· ${m.divisao}`}
                  </p>
                </div>

                {/* Status */}
                <div className="text-right shrink-0">
                  {m.status_pagamento === 'em_dia' && (
                    <span className="inline-block bg-green-500/20 text-green-300 border border-green-500/40 rounded-full px-3 py-1 text-xs font-bold">
                      ✅ Em dia
                    </span>
                  )}
                  {m.status_pagamento === 'atrasada' && (
                    <span className="inline-block bg-red-500/20 text-red-300 border border-red-500/40 rounded-full px-3 py-1 text-xs font-bold">
                      🔴 {m.mensalidades_pendentes} {m.mensalidades_pendentes === 1 ? 'mês' : 'meses'}
                    </span>
                  )}
                  {m.status_pagamento === 'bloqueada' && (
                    <span className="inline-block bg-orange-500/20 text-orange-300 border border-orange-500/40 rounded-full px-3 py-1 text-xs font-bold">
                      🚫 Bloqueada
                    </span>
                  )}
                </div>
              </div>

              {/* Detalhes */}
              <div className="mt-3 pt-3 border-t border-[#2D243F] grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-[#716A80]">Último pagamento</p>
                  <p className={`font-bold ${m.ultimo_pagamento_data ? 'text-[#FFD700]' : 'text-[#716A80]'}`}>
                    {formatarData(m.ultimo_pagamento_data)}
                    {m.ultimo_pagamento_metodo && (
                      <span className="text-[#A19BAA] font-normal ml-1">
                        ({m.ultimo_pagamento_metodo === 'pix' ? 'PIX app' :
                          m.ultimo_pagamento_metodo === 'manual' ? 'manual' :
                          m.ultimo_pagamento_metodo})
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[#716A80]">Total pago no app</p>
                  <p className="text-white font-bold">
                    R$ {(m.total_pago_app || 0).toFixed(2).replace('.', ',')}
                    <span className="text-[#A19BAA] font-normal ml-1">
                      ({m.qtd_pagamentos_app || 0}x)
                    </span>
                  </p>
                </div>
              </div>

              {/* Ações */}
              {m.mensalidades_pendentes > 0 && (
                <div className="mt-3 pt-3 border-t border-[#2D243F] flex gap-2 flex-wrap">
                  <button
                    onClick={() => registrarPagamento(m.id, m.nome_guerra || m.nome_completo)}
                    data-testid={`caderninho-registrar-${m.id}`}
                    className="bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-500/40 font-bold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"
                  >
                    <Check size={14} /> Registrar Pagamento R$60
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ResumoCard = ({ label, valor, cor, border }) => (
  <div className={`bg-[#120E1A] border ${border} rounded-xl p-3 text-center`}>
    <p className="text-[#A19BAA] text-xs">{label}</p>
    <p className={`text-2xl font-black ${cor}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{valor}</p>
  </div>
);

export default AdminDashboard;
